import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import config from 'config';
import { Typography, Button } from 'components/alf-design';
import { customTypes, formats, useAuth0 } from 'utils';
import { METHOD } from 'utils/constants';

import BillingSubtitles from './BillingSubtitles';

const InsuredPaid = ({
  billing,
  showCancellationData,
  handleMakePaymentClick,
  handleHistoryClick,
  disableMakePayments,
  ...insuredProps
}) => {
  const { user } = useAuth0();
  const isAdmin = user
    ? user.email.includes('@slideinsurance.com') &&
      user.role &&
      user.role?.isAdmin
    : false;

  const hasPaymentDue =
    billing.pastDueAmount > 0 || billing.currentDueAmount > 0;

  const showCurrentAmountDue =
    !billing.autoPay &&
    (!billing.pastDueAmount || billing.currentDueAmount) &&
    billing.collectionMethod !== METHOD.PENDING_CARD &&
    billing.collectionMethod !== METHOD.PENDING_BANK;

  const portalRef = useRef(
    document.getElementById('portalOneContainer') ||
      (() => {
        const el = document.createElement('div');
        el.id = 'portalOneContainer';
        return el;
      })(),
  );

  useEffect(() => {
    const el = portalRef.current;
    if (!document.body.contains(el)) {
      document.body.appendChild(el);
    }
  }, []);

  const BillingButtons = () => {
    return (
      <div className="grid grid-cols-2 gap-[12px]">
        {!disableMakePayments && !billing.autoPay && (
          <Button
            type="primary"
            id="payments-button"
            data-dd-action-name="dashboard.billing.make_payment"
            onClick={handleMakePaymentClick}
            disabled={!hasPaymentDue || (isAdmin && config.appEnv === 'PROD')}
          >
            Make a Payment
          </Button>
        )}
        {billing.collectionMethod !== METHOD.PENDING_CARD &&
          billing.collectionMethod !== METHOD.PENDING_BANK && (
            <Button
              id="payment-history-button"
              data-dd-action-name="dashboard.billing.payment_history"
              onClick={handleHistoryClick}
              className={`${disableMakePayments || billing.autoPay ? 'w-full' : ''}`}
              type="secondary"
            >
              Payment History
            </Button>
          )}
      </div>
    );
  };

  return (
    <div>
      <div className="grid mb-[24px]">
        {billing.autoPay && (
          <Typography type="caption1" component="span">
            Your policy is set up as Insured Billed. For questions or assistance
            regarding your payments, please contact your agent.
          </Typography>
        )}
        {!billing.autoPay && disableMakePayments && (
          <Typography type="caption1" component="span">
            For questions or assistance regarding your payments, please contact
            your agent.
          </Typography>
        )}
        {billing.pastDueAmount > 0 &&
          billing.collectionMethod !== METHOD.PENDING_CARD &&
          billing.collectionMethod !== METHOD.PENDING_BANK && (
            <div className="bg-legacy-errorBackground border border-legacy-errorBorder p-[13px] rounded-[8px]">
              <Typography type="error" component="span">
                Your last installment was not paid in full.
              </Typography>
            </div>
          )}
      </div>

      <div className="flex justify-evenly">
        {billing.pastDueAmount > 0 && (
          <div className="grid text-center">
            <Typography type="caption1">Past Due</Typography>
            <Typography
              id="past-due-amount"
              type="money"
              component="h3"
              className="text-legacy-error"
            >
              {formats.moneyFormat(billing.pastDueAmount)}
            </Typography>
          </div>
        )}

        {showCurrentAmountDue &&
        (billing.currentDueAmount > 0 || !billing.pastDueAmount) ? (
          <div className="grid text-center">
            <Typography type="caption1">Amount Due</Typography>
            <Typography id="amount-due" type="money" component="h3">
              {formats.moneyFormat(billing.currentDueAmount)}
            </Typography>
          </div>
        ) : null}

        {billing.currentDueAmount &&
        showCurrentAmountDue &&
        billing.currentDueDate ? (
          <div className="grid text-center">
            <Typography type="caption1">Due Date</Typography>
            <Typography id="current-due-date" type="money" component="h3">
              {formats.dateFormat(billing.currentDueDate)}
            </Typography>
          </div>
        ) : null}

        {showCancellationData && (
          <div className="grid text-center">
            <Typography type="caption1">Cancellation Date</Typography>
            <Typography type="error" component="h3">
              {billing.cancellationDate}
            </Typography>
          </div>
        )}
      </div>

      <hr className="border-t border-gray-200 mt-[32px] mb-[16px]" />
      <BillingSubtitles billing={billing} {...insuredProps} />
      <BillingButtons />

      <div>
        {showCancellationData && (
          <div className="grid grid-rows-2 text-center">
            <Typography type="error" component="span">
              <b>Cancellation Date </b>
            </Typography>
            <Typography type="error">{billing.cancellationDate}</Typography>
          </div>
        )}
      </div>
    </div>
  );
};
InsuredPaid.propTypes = {
  billing: customTypes.billing,
  showCancellationData: PropTypes.bool,
  handleMakePaymentClick: PropTypes.func,
  handleHistoryClick: PropTypes.func,
  disableMakePayments: PropTypes.bool,
};

export default InsuredPaid;
